import { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { IconBillCheck, IconFlask, IconPrinter, IconRefreshCw } from '../../components/layout/icons';
import { SortableTh } from '../../components/ui/SortableTh';
import { doctorNameOrDash } from '../../lib/doctor-name';
import type { TableSortControl } from '../../lib/table-sort';
import { ageDisplay, formatPathologyDateTime, testStatusLabels } from './pathology.utils';
import type { PathologyAction, PathologyBill, PathologyChargeLine } from './types';

interface WorklistSelectionApi {
  isSelected: (billId: string, lineId: string) => boolean;
  onToggleLine: (billId: string, lineId: string) => void;
  onToggleAllInBill: (billId: string, lineIds: string[]) => void;
  selectedInBill: (billId: string) => string[];
}

interface WorklistTableProps extends WorklistSelectionApi {
  bills: PathologyBill[];
  tableSort: TableSortControl;
  expandedBillId: string | null;
  onToggleExpand: (billId: string) => void;
  actionLabel: string | null;
  action: PathologyAction | null;
  actionPending: boolean;
  onAdvance: (billId: string, lineIds: string[]) => void;
  onRollback?: (billId: string, lineIds: string[]) => void;
  showReportUpdate?: boolean;
  reportSelection?: WorklistSelectionApi;
  reportUpdateBulk?: boolean;
  readyReportMode?: boolean;
  onOpenReportUpload?: (billId: string, lineIds: string[]) => void;
  onOpenReportPrint?: (billId: string, lineIds: string[]) => void;
}

const isActionEligible = (line: PathologyChargeLine, action: PathologyAction | null) => {
  if (!action) return false;
  if (action === 'collect') return line.testStatus === 'bill-created';
  if (action === 'lab-receive') return line.testStatus === 'sample-collected';
  if (action === 'generate-report') return line.testStatus === 'lab-received';
  if (action === 'deliver-report') return line.testStatus === 'report-generated';
  if (action === 'mark-ready') return line.testStatus !== 'report-generated' && line.testStatus !== 'report-delivered';
  if (action === 'rollback') return true;
  return false;
};

const actionIconLabel = (action: PathologyAction | null) => {
  if (action === 'collect') return 'C';
  if (action === 'lab-receive') return 'R';
  if (action === 'generate-report' || action === 'mark-ready') return 'V';
  if (action === 'deliver-report') return 'D';
  return '#';
};

export const WorklistTable = ({
  bills, tableSort, expandedBillId, onToggleExpand, isSelected, onToggleLine, onToggleAllInBill, selectedInBill,
  actionLabel, action, actionPending, onAdvance, onRollback, showReportUpdate = false, reportSelection, readyReportMode = false,
  reportUpdateBulk = true, onOpenReportUpload, onOpenReportPrint,
}: WorklistTableProps) => (
  <div className="table-scroll">
    <table className="investigation-bill-table pathology-table">
      <thead>
        <tr>
          <th>#</th>
          <SortableTh control={tableSort} column="billId">Bill NO</SortableTh>
          <SortableTh control={tableSort} column="billDate">Bill Date</SortableTh>
          <SortableTh control={tableSort} column="patient">Patient Name</SortableTh>
          <SortableTh control={tableSort} column="mobile">Mobile</SortableTh>
          <SortableTh control={tableSort} column="age">Age</SortableTh>
          <SortableTh control={tableSort} column="gender">Gender</SortableTh>
          <th>Bed</th>
          <SortableTh control={tableSort} column="doctor">Ref. Doctor/Under Doctor</SortableTh>
        </tr>
      </thead>
      <tbody>
        {bills.map((bill) => {
          const expanded = expandedBillId === bill.id;
          const selectedLineIds = selectedInBill(bill.id);
          const actionLineIds = bill.charges.filter((line) => isActionEligible(line, action)).map((line) => line.id);
          const allChecked = actionLineIds.length > 0 && actionLineIds.every((lineId) => isSelected(bill.id, lineId));
          const allSelected = bill.charges.length > 0 && bill.charges.every((line) => isSelected(bill.id, line.id));
          const reportSelected = reportSelection?.selectedInBill(bill.id) ?? [];
          const allReportChecked = bill.charges.length > 0 && bill.charges.every((line) => Boolean(reportSelection?.isSelected(bill.id, line.id)));

          return (
            <Fragment key={bill.id}>
              <tr className="pathology-bill-row">
                <td><button type="button" className={`pathology-expand-toggle${expanded ? ' open' : ''}`} onClick={() => onToggleExpand(bill.id)} aria-label={expanded ? 'Collapse' : 'Expand'} aria-expanded={expanded}>{expanded ? '-' : '+'}</button></td>
                <td><Link className="bill-id-link" to={`/investigation-bill/${bill.id}`} title="Open investigation bill"><strong>{bill.billNumber}</strong></Link><span className="pathology-section-badge">{bill.section ?? 'INVESTIGATION'}</span></td>
                <td>{formatPathologyDateTime(bill.billDate)}</td>
                <td>{bill.patientId ? <Link className="patient-name-link" to={`/patients/${bill.patientId}`} title="Open patient profile"><strong>{bill.patientSnapshot.name}</strong></Link> : <strong>{bill.patientSnapshot.name}</strong>}{bill.patientSnapshot.uhid ? <small>({bill.patientSnapshot.uhid})</small> : null}</td>
                <td>{bill.patientSnapshot.phone ?? '-'}</td>
                <td>{ageDisplay(bill.patientSnapshot)}</td>
                <td>{bill.patientSnapshot.gender}</td>
                <td>{bill.bedName ?? '-'}</td>
                <td>{doctorNameOrDash(bill.referringDoctor?.name)}</td>
              </tr>
              {expanded ? (
                <tr className="pathology-detail-row">
                  <td colSpan={9}>
                    <div className="pathology-detail-panel">
                      {readyReportMode ? (
                        <table className="pathology-line-table ready-report-line-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>
                                <input type="checkbox" checked={allSelected} onChange={() => onToggleAllInBill(bill.id, bill.charges.map((line) => line.id))} aria-label="Select all reports for printing" />
                                <button type="button" className="pathology-bulk-action" disabled={selectedLineIds.length === 0} onClick={() => onOpenReportPrint?.(bill.id, selectedLineIds)}><IconPrinter /> Bulk Report Print</button>
                              </th>
                              <th>Attachment</th>
                              <th>Date</th>
                              <th>Test Name</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bill.charges.map((line) => (
                              <tr key={line.id}>
                                <td>
                                  <button type="button" className="pathology-quick-action" title="Report Delivered" disabled={actionPending || !isActionEligible(line, action)} onClick={() => onAdvance(bill.id, [line.id])}>
                                    <IconBillCheck />
                                  </button>
                                </td>
                                <td><input type="checkbox" checked={isSelected(bill.id, line.id)} onChange={() => onToggleLine(bill.id, line.id)} aria-label={`Select ${line.chargeName} for print`} /></td>
                                <td>{line.attachment ? <a className="pathology-icon-link" href={line.attachment.dataUrl} download={line.attachment.name} title="Download attachment">Download</a> : null}</td>
                                <td>{formatPathologyDateTime(bill.billDate)}</td>
                                <td>{line.chargeName}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <table className="pathology-line-table">
                          <thead>
                            <tr>
                              {actionLabel ? (
                                <th>
                                  <input type="checkbox" checked={allChecked} onChange={() => onToggleAllInBill(bill.id, actionLineIds)} aria-label="Select all tests" />
                                  <button type="button" className="pathology-bulk-action" disabled={selectedLineIds.length === 0 || actionPending} onClick={() => onAdvance(bill.id, selectedLineIds)}>{actionLabel}</button>
                                </th>
                              ) : null}
                              {showReportUpdate ? (
                                <th>
                                  {reportUpdateBulk ? (
                                    <>
                                      <input type="checkbox" checked={allReportChecked} onChange={() => reportSelection?.onToggleAllInBill(bill.id, bill.charges.map((line) => line.id))} aria-label="Select all tests for report update" />
                                      <button type="button" className="pathology-bulk-action" disabled={reportSelected.length === 0} onClick={() => onOpenReportUpload?.(bill.id, reportSelected)}>Update</button>
                                    </>
                                  ) : '#'}
                                </th>
                              ) : null}
                              <th>Date</th><th>Test Name</th><th>Current Status</th><th>Samples</th><th>Vial</th><th>Barcode</th>{onRollback ? <th>Rollback</th> : null}
                            </tr>
                          </thead>
                          <tbody>
                            {bill.charges.map((line) => (
                              <tr key={line.id}>
                                {actionLabel ? (
                                  <td>
                                    <input type="checkbox" checked={isSelected(bill.id, line.id)} disabled={!isActionEligible(line, action)} onChange={() => onToggleLine(bill.id, line.id)} aria-label={`Select ${line.chargeName}`} />
                                    <button type="button" className="pathology-quick-action" title={actionLabel} disabled={actionPending || !isActionEligible(line, action)} onClick={() => onAdvance(bill.id, [line.id])}>
                                      <span>{actionIconLabel(action)}</span>
                                    </button>
                                  </td>
                                ) : null}
                                {showReportUpdate ? (
                                  <td>
                                    {reportUpdateBulk ? <input type="checkbox" checked={Boolean(reportSelection?.isSelected(bill.id, line.id))} onChange={() => reportSelection?.onToggleLine(bill.id, line.id)} aria-label={`Select ${line.chargeName} for update`} /> : null}
                                    <button type="button" className="pathology-quick-action" title="Add / Edit Result" onClick={() => onOpenReportUpload?.(bill.id, [line.id])}>
                                      <IconFlask />
                                    </button>
                                  </td>
                                ) : null}
                                <td>{formatPathologyDateTime(bill.billDate)}</td>
                                <td>{line.chargeName}</td>
                                <td><span className={`pathology-status-badge ${line.testStatus}`}>{testStatusLabels[line.testStatus]}</span></td>
                                <td>{line.sample ?? ''}</td>
                                <td>{line.vial ?? ''}</td>
                                <td>{line.barcode ? <span className="pathology-barcode" title={line.barcode} aria-label={`Barcode ${line.barcode}`} /> : null}</td>
                                {onRollback ? (
                                  <td>
                                    <button type="button" className="pathology-quick-action" title="Rollback" disabled={actionPending} onClick={() => onRollback(bill.id, [line.id])}>
                                      <IconRefreshCw />
                                    </button>
                                  </td>
                                ) : null}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </td>
                </tr>
              ) : null}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  </div>
);
